import type { IsoDate } from "../domain/types.js";
import type { RacePredictionRow } from "../../shared/types.js";
import { canBetOnRace } from "../../shared/race-prediction.js";

/**
 * 台北馬賽季競猜的樂觀更新。
 *
 * 與 daily-log-store、cheer-store 同一條 seam：writer 由外部注入，實作吸收
 * 樂觀寫入、回滾與規則判斷。**送出之前先問日期規則**，才說得出「為什麼不能下注」
 * 而不是丟一個 403 出來。
 *
 * 前端擋只是為了當場說明，真正擋住的是後端；兩邊呼叫的是
 * `shared/race-prediction.ts` 的同一支函式。
 *
 * **這個 module 只管寫入。** 「我的那一注是哪一筆」是 `src/domain/supporter.ts` 的事。
 */

export interface RaceBet {
  willFinish: boolean;
  willReachWeight: boolean;
  /** 兩個數字都選填——只想表態的人不必想一個數字。 */
  weightKg: number | null;
  finishSeconds: number | null;
}

export interface RacePredictionWriter {
  save(nickname: string, bet: RaceBet): Promise<unknown>;
}

export interface RacePredictionStoreOptions {
  writer: RacePredictionWriter;
  onChange: () => void;
}

export type RacePredictionResult = { ok: true } | { ok: false; error: string };

export interface RacePredictionStore {
  readonly predictions: RacePredictionRow[];
  replaceAll(rows: RacePredictionRow[]): void;
  /** 下注。立刻反映在 predictions，失敗會自動回滾。 */
  bet(nickname: string, bet: RaceBet, today: IsoDate): Promise<RacePredictionResult>;
}

export function createRacePredictionStore({
  writer,
  onChange
}: RacePredictionStoreOptions): RacePredictionStore {
  let predictions: RacePredictionRow[] = [];

  return {
    get predictions() {
      return predictions;
    },

    replaceAll(rows) {
      predictions = rows;
    },

    async bet(nickname, bet, today) {
      if (!nickname) return { ok: false, error: "還沒有名字" };
      if (!canBetOnRace(today)) return { ok: false, error: "競猜已經封盤了。" };

      const before = predictions;
      const next: RacePredictionRow = {
        nickname,
        will_finish: bet.willFinish ? 1 : 0,
        will_reach_weight: bet.willReachWeight ? 1 : 0,
        weight_kg: bet.weightKg,
        finish_seconds: bet.finishSeconds
      };
      // 一人一筆，所以是覆蓋而不是追加。照暱稱排，與 API 回來的順序一致。
      predictions = [...before.filter(row => row.nickname !== nickname), next]
        .sort((a, b) => a.nickname.localeCompare(b.nickname));
      onChange();

      try {
        await writer.save(nickname, bet);
        return { ok: true };
      } catch (error) {
        predictions = before;
        onChange();
        return { ok: false, error: error instanceof Error ? error.message : "下注失敗" };
      }
    }
  };
}
